<template>
  <div class="catalog-filters-container">
    <!-- Mobile Filter Button -->
    <div class="mobile-filter-trigger" @click="openMobileFilters">
      <i class="uil-filter"></i>
      <span>{{ $t("general.filter") || "Filtreler" }}</span>
      <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
    </div>

    <!-- Desktop: inline sidebar / Mobile: AppModal -->
    <AppModal
      :close-on-overlay="true"
      :inline="!isMobile"
      :show-close-button="false"
      :visible.sync="isMobileFilterOpen"
      width="100%"
      @close="closeMobileFilters"
    >
      <div v-show="isFilterVisible" class="filter-panel"
           :class="{
             'is-mobile-open': isMobileFilterOpen,
             'filter-panel-mobile': isMobile,
             'filter-panel-desktop': !isMobile
           }">
        <!-- Mobile Header -->
        <div class="filter-panel-header">
          <h4>{{ $t("general.filter") || "Filtrele" }}</h4>
          <button class="close-btn" @click="closeMobileFilters">
            <i class="uil-times"></i>
          </button>
        </div>

        <!-- Filter Content -->
        <div class="filter-panel-content">
          <ul class="filter-accordion" uk-accordion="multiple:true">
            <!-- Platform Trainings -->
            <li :class="{ 'uk-open': hasUrlFilters }">
              <a class="uk-accordion-title filter-section-title" href="#">
                <i class="uil-book-alt"></i>
                <span v-if="subdomain === 'ato.softinya.com' || subdomain === 'akademi.atonet.org.tr'">
                  ATO Eğitimleri
                </span>
                <span v-else>{{ $t("general.platform_trainings") }}</span>
              </a>
              <div class="uk-accordion-content">
                <ul class="filter-options">
                  <li v-for="(item, index) in educationTypes" :key="index">
                    <label class="filter-option" :class="{ 'is-checked': options.includes(item.id) }">
                      <input :id="`checkboxEducation-${index}`"
                             :checked="options.includes(item.id)"
                             :disabled="catalogsLoading"
                             :value="item.id"
                             type="checkbox"
                             @change="handleOptionsChange($event, item.id)"/>
                      <span class="checkmark">
                        <i class="uil-check"></i>
                      </span>
                      <span class="option-text">{{ item.name }}</span>
                    </label>
                  </li>
                </ul>
              </div>
            </li>

            <!-- Institution Trainings -->
            <li :class="{ 'uk-open': hasUrlFilters }">
              <a class="uk-accordion-title filter-section-title" href="#">
                <i class="uil-book-alt"></i>
                <span>{{ $t("general.institution_trainings") }}</span>
              </a>
              <div class="uk-accordion-content">
                <ul class="filter-options">
                  <li v-for="(item, index) in institutionTraining" :key="'institutionTraining-' + index">
                    <label class="filter-option" :class="{ 'is-checked': options.includes(item.id) }">
                      <input :id="`checkboxInstitution-${index}`"
                             :checked="options.includes(item.id)"
                             :disabled="catalogsLoading"
                             :value="item.id"
                             type="checkbox"
                             @change="handleOptionsChange($event, item.id)"/>
                      <span class="checkmark">
                        <i class="uil-check"></i>
                      </span>
                      <span class="option-text">{{ item.name }}</span>
                    </label>
                  </li>
                </ul>
              </div>
            </li>

            <!-- Training Types -->
            <li :class="{ 'uk-open': hasUrlFilters }">
              <a class="uk-accordion-title filter-section-title" href="#">
                <i class="uil-notes"></i>
                <span>{{ $t("general.training_types") }}</span>
              </a>
              <div class="uk-accordion-content">
                <ul class="filter-options">
                  <li v-for="(item, index) in formattedTrainingKind" :key="'formattedTrainingKind-' + index">
                    <label class="filter-option" :class="{ 'is-checked': kinds.includes(item.id) }">
                      <input :id="`checkboxTrainingKind-${index}`"
                             :checked="kinds.includes(item.id)"
                             :disabled="catalogsLoading"
                             :value="item.id"
                             type="checkbox"
                             @change="handleKindsChange($event, item.id)"/>
                      <span class="checkmark">
                        <i class="uil-check"></i>
                      </span>
                      <span class="option-text">{{ item.name }}</span>
                    </label>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>

        <!-- Mobile Footer -->
        <div class="filter-panel-footer">
          <button class="btn-clear" @click="clearAllFilters">
            <i class="uil-trash-alt"></i>
            {{ $t("general.clear") || "Temizle" }}
          </button>
          <button class="btn-apply" @click="applyAndClose">
            <i class="uil-check"></i>
            {{ $t("general.apply") || "Uygula" }}
          </button>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script>
import _ from "lodash";
import AppModal from "@/view/components/ui/AppModal.vue";

export default {
  name: "CatalogFilters",
  components: {
    AppModal
  },
  props: {
    subdomain: {
      type: String,
      default: null
    },
    educationTypes: {
      type: Array,
      default: () => []
    },
    institutionTraining: {
      type: Array,
      default: () => []
    },
    formattedTrainingKind: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    kinds: {
      type: Array,
      default: () => []
    },
    catalogsLoading: {
      type: Boolean,
      default: false
    },
    hasUrlFilters: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isMobileFilterOpen: false,
      windowWidth: window.innerWidth
    };
  },

  computed: {
    isMobile() {
      return this.windowWidth < 960;
    },
    isFilterVisible() {
      return !this.isMobile || this.isMobileFilterOpen;
    },
    activeFilterCount() {
      const optionsCount = this.options.filter(opt => opt !== 0).length;
      const kindsCount = this.kinds.length;
      return optionsCount + kindsCount;
    }
  },

  created() {
    // Tek bir debounce fonksiyonu - her tıklamada timer sıfırlanır
    // Son tıklamadan 800ms sonra istek atılır
    this.debouncedApplyFilter = _.debounce(() => {
      this.$emit('apply-filter');
    }, 800);
  },

  mounted() {
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.debouncedApplyFilter) {
      this.debouncedApplyFilter.cancel();
    }
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
      if (!this.isMobile && this.isMobileFilterOpen) {
        this.isMobileFilterOpen = false;
        document.body.style.overflow = '';
      }
    },

    openMobileFilters() {
      this.isMobileFilterOpen = true;
      document.body.style.overflow = 'hidden';
    },

    closeMobileFilters() {
      this.isMobileFilterOpen = false;
      document.body.style.overflow = '';
    },

    applyAndClose() {
      this.$emit('apply-filter');
      this.closeMobileFilters();
    },

    clearAllFilters() {
      this.$emit('update:options', [0]);
      this.$emit('update:kinds', []);
      this.$nextTick(() => {
        this.debouncedApplyFilter();
      });
    },

    handleOptionsChange(event, itemId) {
      const checked = event.target.checked;
      const newOptions = [...this.options];

      if (checked) {
        if (!newOptions.includes(itemId)) {
          newOptions.push(itemId);
        }
      } else {
        const index = newOptions.indexOf(itemId);
        if (index > -1) {
          newOptions.splice(index, 1);
        }
      }

      this.$emit('update:options', newOptions);
      this.$nextTick(() => {
        this.debouncedApplyFilter();
      });
    },

    handleKindsChange(event, itemId) {
      const checked = event.target.checked;
      const newKinds = [...this.kinds];

      if (checked) {
        if (!newKinds.includes(itemId)) {
          newKinds.push(itemId);
        }
      } else {
        const index = newKinds.indexOf(itemId);
        if (index > -1) {
          newKinds.splice(index, 1);
        }
      }

      this.$emit('update:kinds', newKinds);
      this.$nextTick(() => {
        this.debouncedApplyFilter();
      });
    }
  }
}
</script>

<style scoped>
/* ==========================================================================
   Container
   ========================================================================== */
.catalog-filters-container {
  position: relative;
}

/* ==========================================================================
   Mobile Filter Trigger Button
   ========================================================================== */
.mobile-filter-trigger {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #3e416d;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.mobile-filter-trigger:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.mobile-filter-trigger:active {
  transform: scale(0.98);
}

.mobile-filter-trigger i {
  font-size: 18px;
  color: var(--backgroundColor, #667eea);
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--backgroundColor, #667eea);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
}

/* ==========================================================================
   Overlay
   ========================================================================== */
.filter-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

/* ==========================================================================
   Filter Panel
   ========================================================================== */
.filter-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: none;
  overflow: hidden;
}

.filter-panel-header {
  display: none;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.filter-panel-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f1f5f9;
}

.close-btn i {
  font-size: 20px;
  color: #64748b;
}

/* ==========================================================================
   Filter Content
   ========================================================================== */
.filter-panel-content {
  padding: 0;
  max-height: none;
  overflow-y: auto;
}

.filter-accordion {
  list-style: none;
  padding: 0;
  margin: 0;
}

.filter-accordion > li {
  border-bottom: 1px solid #e2e8f0;
  margin-top: 0 !important;
}

.filter-accordion > li:last-child {
  border-bottom: none;
}

/* ==========================================================================
   Accordion Title
   ========================================================================== */
.filter-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #3e416d;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  text-decoration: none !important;
}

.filter-section-title:hover {
  background: #f7fafc;
}

.filter-section-title i {
  font-size: 18px;
  color: var(--backgroundColor, #667eea);
  flex-shrink: 0;
}

.filter-section-title span {
  flex: 1;
  line-height: 1.4;
}

.uk-open > .filter-section-title {
  background: #f7fafc;
}

/* ==========================================================================
   Filter Options
   ========================================================================== */
.filter-options {
  list-style: none;
  padding: 8px 12px 12px;
  margin: 0;
  max-height: 280px;
  overflow-y: auto;
}

.filter-options::-webkit-scrollbar {
  width: 5px;
}

.filter-options::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.filter-options::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.filter-options::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.filter-options li {
  margin-bottom: 4px;
}

.filter-options li:last-child {
  margin-bottom: 0;
}

/* ==========================================================================
   Filter Option Label
   ========================================================================== */
.filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.filter-option:hover {
  background: #f1f5f9;
}

.filter-option input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.filter-option .checkmark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #ffffff;
  border: 2px solid #cbd5e0;
  border-radius: 4px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.filter-option .checkmark i {
  font-size: 12px;
  color: #ffffff;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s ease;
}

.filter-option.is-checked .checkmark {
  background: var(--backgroundColor, #667eea);
  border-color: var(--backgroundColor, #667eea);
}

.filter-option.is-checked .checkmark i {
  opacity: 1;
  transform: scale(1);
}

.filter-option .option-text {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.4;
  word-break: break-word;
}

.filter-option.is-checked .option-text {
  color: #2d3748;
  font-weight: 500;
}

.filter-option input[type="checkbox"]:disabled + .checkmark {
  opacity: 0.5;
  cursor: not-allowed;
}

.filter-option input[type="checkbox"]:disabled ~ .option-text {
  opacity: 0.5;
}

/* ==========================================================================
   Filter Panel Footer
   ========================================================================== */
.filter-panel-footer {
  display: none;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn-clear,
.btn-apply {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-clear {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.btn-clear:hover {
  background: #f1f5f9;
  color: #475569;
}

.btn-apply {
  background: var(--backgroundColor, #667eea);
  color: #ffffff;
}

.btn-apply:hover {
  opacity: 0.9;
}

.btn-clear i,
.btn-apply i {
  font-size: 16px;
}

/* ==========================================================================
   Transitions
   ========================================================================== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(-100%);
}

/* ==========================================================================
   Responsive - Mobile (< 960px)
   ========================================================================== */
@media (max-width: 959px) {
  .catalog-filters-container {
    width: 100%;
    margin-bottom: 16px;
  }

  .mobile-filter-trigger {
    display: flex;
  }

  .filter-overlay {
    display: block;
  }

  .filter-panel {
    position: fixed;
    top: 0;
    left: 0;
    width: 85%;
    max-width: 320px;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    box-sizing: border-box;
  }

  .filter-panel.is-mobile-open {
    transform: translateX(0);
  }

  .filter-panel-header {
    display: flex;
  }

  .filter-panel-content {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .filter-panel-footer {
    display: flex;
  }

  .filter-section-title {
    padding: 16px;
    font-size: 15px;
  }

  .filter-options {
    padding: 8px 14px 14px;
    max-height: none;
  }

  .filter-option {
    padding: 10px 12px;
  }

  .filter-option .checkmark {
    width: 22px;
    height: 22px;
  }

  .filter-option .option-text {
    font-size: 15px;
  }

  .filter-panel-header {
    min-height: 64px;
    box-sizing: border-box;
    position: relative;
  }

  .close-btn {
    width: 44px;
    height: 44px;
    min-width: 44px;
    margin-left: auto;
  }

  .filter-panel-footer {
    padding-bottom: max(16px, env(safe-area-inset-bottom));
  }
}

/* ==========================================================================
   Responsive - Small Mobile (< 480px)
   ========================================================================== */
@media (max-width: 479px) {
  .filter-panel {
    width: 100%;
    max-width: 100%;
  }

  .mobile-filter-trigger {
    padding: 14px 16px;
  }

  .filter-panel-header h4 {
    font-size: 16px;
  }

  .filter-section-title {
    font-size: 14px;
  }

  .filter-option .option-text {
    font-size: 14px;
  }
}

/* ==========================================================================
   Desktop (>= 960px)
   ========================================================================== */
@media (min-width: 960px) {
  .filter-panel {
    position: sticky;
    top: 100px;
  }

  .slide-enter,
  .slide-leave-to {
    transform: translateX(0);
  }
}

/* Desktop ve mobil panel görsel olarak ayrı davranır. */
@media (max-width: 959px) {
  .catalog-filters-container :deep(.app-modal-overlay) {
    align-items: center;
    justify-content: center;
    padding: calc(16px + env(safe-area-inset-top)) 12px
      calc(16px + env(safe-area-inset-bottom));
  }

  .catalog-filters-container :deep(.app-modal) {
    width: 100% !important;
    max-width: 480px !important;
    max-height: calc(100dvh - 32px) !important;
    border-radius: 16px;
    overflow: hidden;
  }

  .catalog-filters-container :deep(.app-modal-body) {
    padding: 0;
    overflow: hidden;
  }

  .catalog-filters-container .filter-panel-mobile {
    position: relative;
    inset: auto;
    width: 100%;
    max-width: none;
    height: auto;
    max-height: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    transform: none !important;
  }

  .catalog-filters-container .filter-panel-mobile.is-mobile-open {
    transform: none !important;
  }

  .catalog-filters-container .filter-panel-mobile .filter-panel-header {
    min-height: 64px;
    padding: 12px 16px;
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
  }

  .catalog-filters-container .filter-panel-mobile .filter-panel-header h4 {
    font-size: 18px;
  }

  .catalog-filters-container .filter-panel-mobile .filter-panel-content {
    max-height: min(60vh, 520px);
    overflow-y: auto;
  }

  .catalog-filters-container .filter-panel-mobile .filter-panel-footer {
    display: flex;
    gap: 8px;
    padding: 12px 16px max(12px, env(safe-area-inset-bottom));
    background: #ffffff;
  }

  .catalog-filters-container .filter-panel-mobile .btn-clear,
  .catalog-filters-container .filter-panel-mobile .btn-apply {
    min-height: 48px;
    border-radius: 10px;
  }
}

/* ==========================================================================
   UIKit Accordion Content Override
   ========================================================================== */
.uk-accordion-content {
  margin-top: 0 !important;
  padding: 0;
}
</style>
